import ExcelJS from 'exceljs';

import type { CartItem } from '../context/cart';
import { formatCNPJ } from '../functions';

// HubMaster brand purple
const BRAND_HEX = '8E51FF';
const BRAND_LIGHT = 'F3EEFF';
const GRAY_ROW = 'F9F9F9';
const FOOTER_BG = 'EDE0FF';

interface Props {
	excel: {
		title: string;
		isPurchaseOrder: boolean;
		generatedAt: string;
		emitente?: {
			fantasia?: string;
			cnpj?: string;
		};
	};
	items: CartItem[];
}

function brandFill(hex: string): ExcelJS.Fill {
	return { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } };
}

function applyHeaderStyle(cell: ExcelJS.Cell) {
	cell.fill = brandFill(BRAND_HEX);
	cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
	cell.alignment = { vertical: 'middle', horizontal: 'center' };
	cell.border = {
		bottom: { style: 'thin', color: { argb: 'FF6B33CC' } },
	};
}

function applyFooterStyle(cell: ExcelJS.Cell, isValue = false) {
	cell.fill = brandFill(FOOTER_BG);
	cell.font = { bold: true, color: { argb: `FF${BRAND_HEX}` }, size: 10 };
	cell.alignment = {
		vertical: 'middle',
		horizontal: isValue ? 'right' : 'left',
	};
}

export async function generateOrderExcel({ excel, items }: Props) {
	const wb = new ExcelJS.Workbook();
	wb.creator = 'HubMaster';
	wb.calcProperties.fullCalcOnLoad = true;
	const ws = wb.addWorksheet('Pedido', {
		pageSetup: { fitToPage: true, fitToWidth: 1 },
	});

	// ── Larguras ──────────────────────────────────────────────────────────────
	ws.columns = [
		{ key: 'num', width: 5 },
		{ key: 'cod', width: 13 },
		{ key: 'ean', width: 18 },
		{ key: 'name', width: 42 },
		{ key: 'qty', width: 9 },
		{ key: 'unit', width: 9 },
		{ key: 'price', width: 17 },
		{ key: 'total', width: 17 },
	];

	// ── Linha 1: título principal ─────────────────────────────────────────────
	ws.mergeCells('A1:H1');
	const titleCell = ws.getCell('A1');
	titleCell.value = excel.title.toUpperCase();
	titleCell.fill = brandFill(BRAND_HEX);
	titleCell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 14 };
	titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
	ws.getRow(1).height = 30;

	// ── Linhas de metadados ───────────────────────────────────────────────────
	const meta: [string, string][] = [];
	if (excel.emitente?.fantasia)
		meta.push(['Emitente', excel.emitente.fantasia]);
	if (excel.emitente?.cnpj)
		meta.push(['CNPJ', formatCNPJ(excel.emitente.cnpj)]);
	meta.push(['Gerado em', excel.generatedAt]);

	for (const [label, value] of meta) {
		const row = ws.addRow([]);
		ws.mergeCells(`A${row.number}:H${row.number}`);
		const cell = ws.getCell(`A${row.number}`);
		cell.value = `${label}: ${value}`;
		cell.fill = brandFill(BRAND_LIGHT);
		cell.font = { color: { argb: `FF${BRAND_HEX}` }, size: 9 };
		cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
		row.height = 16;
	}

	// Espaço
	ws.addRow([]);

	// ── Cabeçalho da tabela ───────────────────────────────────────────────────
	const headerValues = excel.isPurchaseOrder
		? ['#', 'Código', 'EAN', 'Produto', 'Qtd', 'Un.', 'Preço Unit.', 'Total']
		: ['#', 'Código', 'EAN', 'Produto', 'Qtd', 'Un.', 'Preço', 'Subtotal'];

	const headerRow = ws.addRow(headerValues);
	headerRow.height = 20;
	headerRow.eachCell((cell) => applyHeaderStyle(cell));

	// ── Linhas de dados ───────────────────────────────────────────────────────
	items.forEach((item, i) => {
		const qty = item.isWeight
			? Number(item.quantity.toFixed(2))
			: item.quantity;
		const rowData = excel.isPurchaseOrder
			? [i + 1, item.id, item.barcode, item.name, qty, item.unit ?? 'un', 0, '']
			: [
					i + 1,
					item.id,
					item.barcode,
					item.name,
					qty,
					item.unit ?? 'un',
					item.price,
					'',
				];

		const row = ws.addRow(rowData);
		row.height = 18;
		const isOdd = i % 2 === 0;

		row.getCell(7).numFmt = 'R$ #,##0.00';
		row.getCell(8).value = { formula: `E${row.number}*G${row.number}` };
		row.getCell(8).numFmt = 'R$ #,##0.00';

		row.eachCell({ includeEmpty: true }, (cell, colNum) => {
			if (isOdd) cell.fill = brandFill(GRAY_ROW);

			cell.font = { size: 9 };
			cell.alignment = { vertical: 'middle' };

			cell.border = {
				bottom: { style: 'hair', color: { argb: 'FFE0E0E0' } },
			};

			// alinhamentos específicos
			if (colNum === 1)
				cell.alignment = { ...cell.alignment, horizontal: 'center' };
			if (colNum >= 5)
				cell.alignment = { ...cell.alignment, horizontal: 'right' };
		});

		// Linha de preenchimento manual para ordem de compra
		if (excel.isPurchaseOrder) {
			const priceCell = row.getCell(7);
			[priceCell].forEach((c) => {
				c.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: { argb: 'FFFFF9E6' },
				};
				// c.border = {
				// 	bottom: { style: 'thin', color: { argb: 'FFCCAA00' } },
				// };
			});
		}
	});

	// ── Rodapé de total ────────────────────────────────────────────────────────
	const firstDataRow = headerRow.number + 1;
	const lastDataRow = headerRow.number + items.length;

	const footRow = ws.addRow(['', '', '', '', '', '', 'TOTAL', '']);
	footRow.height = 20;
	footRow.getCell(8).value = {
		formula: `SUM(H${firstDataRow}:H${lastDataRow})`,
	};
	footRow.getCell(8).numFmt = 'R$ #,##0.00';
	footRow.eachCell({ includeEmpty: true }, (cell, colNum) => {
		applyFooterStyle(cell, colNum >= 7);
	});
	ws.mergeCells(`A${footRow.number}:F${footRow.number}`);

	// ── Linha de observações ──────────────────────────────────────────────
	ws.addRow([]);
	const obsRow = ws.addRow(['OBSERVAÇÕES:']);
	ws.mergeCells(`A${obsRow.number}:B${obsRow.number}`);
	ws.mergeCells(`C${obsRow.number}:H${obsRow.number + 1}`);
	const obsCell = ws.getCell(`A${obsRow.number}`);
	obsCell.font = { bold: true, size: 10 };
	obsCell.alignment = { horizontal: 'right', indent: 1 };
	const obsCellEdit = ws.getCell(`C${obsRow.number}`);
	obsCellEdit.font = {
		size: 8,
	};
	obsCellEdit.alignment = {
		horizontal: 'left',
		indent: 0.5,
		vertical: 'top',
	};
	obsCellEdit.border = {
		top: { style: 'thin', color: { argb: 'FFCCAA00' } },
		left: { style: 'thin', color: { argb: 'FFCCAA00' } },
		bottom: { style: 'thin', color: { argb: 'FFCCAA00' } },
		right: { style: 'thin', color: { argb: 'FFCCAA00' } },
	};

	if (excel.isPurchaseOrder) {
		// ── Nota para ordem de compra ──────────────────────────────────────────────
		ws.addRow([]);
		const noteRow = ws.addRow([
			'* Preencha os campos Preço Unit. devolva para orçamento.',
		]);
		ws.mergeCells(`A${noteRow.number}:H${noteRow.number}`);
		const noteCell = ws.getCell(`A${noteRow.number}`);
		noteCell.font = { italic: true, color: { argb: 'FF92400E' }, size: 8 };
		noteCell.alignment = { horizontal: 'left', indent: 1 };
	}

	// ── Rodapé "Powered by HubMaster" ─────────────────────────────────────────
	ws.addRow([]);
	const poweredRow = ws.addRow(['Powered by HubMaster • hubmaster.com.br']);
	ws.mergeCells(`A${poweredRow.number}:H${poweredRow.number}`);
	const poweredCell = ws.getCell(`A${poweredRow.number}`);
	poweredCell.font = {
		italic: true,
		color: { argb: `FF${BRAND_HEX}` },
		size: 8,
	};
	poweredCell.alignment = { horizontal: 'right' };

	// ── Gerar buffer ───────────────────────────────────────────────────────────
	const filename = `${excel.title.replace(/\s+/g, '-')}-${new Date()
		.toLocaleDateString('pt-BR')
		.replace(/\//g, '-')}.xlsx`;

	const buffer = await wb.xlsx.writeBuffer();
	const blob = new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});

	return {
		download: () => {
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			a.click();
			setTimeout(() => URL.revokeObjectURL(url), 60_000);
		},
		share: async () => {
			const file = new File([blob], filename, { type: blob.type });

			if (navigator.canShare?.({ files: [file] })) {
				try {
					await navigator.share({ files: [file], title: excel.title });
					return;
				} catch {
					// cancelled or unsupported → fallback to download
				}
			}

			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			a.click();
			setTimeout(() => URL.revokeObjectURL(url), 60_000);
		},
	};
}
